import React, { useCallback, useEffect, useRef, useState } from "react";

// Modules
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";

// App — deliberately no getOnlineNode/postNode: the portal is anonymous and
// cross-origin; the app helpers attach auth interceptors (401 → logout) and
// hal_json formats that must not run here. Seller preview is the exception:
// it sends the session cookie explicitly via withCredentials.
import { serverUrl } from "../../config";
import useRoomTracking, { getVisitorId } from "./useRoomTracking";
import RoomItemViewer from "./roomItemViewer";
import RoomThread from "./roomThread";

// UI components
import { Col, Container, Row } from "react-bootstrap";
import {
  Button,
  Checkbox,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import SlideshowIcon from "@mui/icons-material/SlideshowOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import MovieIcon from "@mui/icons-material/MovieOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import MicIcon from "@mui/icons-material/MicNoneRounded";
import CollectionsIcon from "@mui/icons-material/CollectionsOutlined";
import ArticleIcon from "@mui/icons-material/ArticleOutlined";
import EventIcon from "@mui/icons-material/CalendarTodayOutlined";
import OpenNewIcon from "@mui/icons-material/OpenInNewOutlined";
import LockIcon from "@mui/icons-material/LockOutlined";
import ChatIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import { Badge, IconButton } from "@mui/material";
import CompanyappLoader from "../../partials/companyappLoader";
import caLogo from "../../assets/ca-logo-pwa.png";

const typeMeta = {
  presentation: { icon: SlideshowIcon, label: "Presentation" },
  document: { icon: DescriptionIcon, label: "Document" },
  video: { icon: MovieIcon, label: "Video" },
  link: { icon: LinkIcon, label: "Link" },
  podcast: { icon: MicIcon, label: "Podcast" },
  gallery: { icon: CollectionsIcon, label: "Gallery" },
  news: { icon: ArticleIcon, label: "Article" },
  event: { icon: EventIcon, label: "Event" },
};

// Types that open in a new tab via their public single screens.
const newTabRoutes = {
  presentation: (item) =>
    `/presentations/view/${item.id}${
      item.access_tokens?.[0]?.token ? `?hash=${item.access_tokens[0].token}` : ""
    }`,
  news: (item) => `/news/${item.id}?hash=${item.access_tokens || ""}`,
  event: (item) => `/events/${item.id}?hash=${item.access_tokens || ""}`,
};

const errorCopy = {
  room_not_found: {
    title: "Room not found",
    body: "This link doesn't match an active sales room. Check the link or contact the person who shared it with you.",
  },
  room_not_available: {
    title: "Room not available",
    body: "This room isn't open right now. Contact the person who shared it with you for an updated link.",
  },
  room_expired: {
    title: "Room expired",
    body: "Access to this room has expired. Contact the person who shared it with you to request renewed access.",
  },
  network: {
    title: "Something went wrong",
    body: "We couldn't load the room. Please try again in a moment.",
  },
};

const IDENTITY_KEY = "dsr_buyer_identity";

const loadIdentity = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(IDENTITY_KEY));
    if (stored && stored.name && stored.email) {
      return stored;
    }
  } catch (e) {
    // fall through
  }
  return { name: "", email: "" };
};

export default function BuyerRoom() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "1";

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which unlock gate to show: null | "password" | "email".
  const [gate, setGate] = useState(null);
  // Email gate stage: "input" (asking for the email) | "sent" (link emailed).
  const [gateStage, setGateStage] = useState("input");
  const [password, setPassword] = useState("");
  const [gateEmail, setGateEmail] = useState("");
  const [gateError, setGateError] = useState(null);
  const [room, setRoom] = useState(null);
  const [openItem, setOpenItem] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messagesOpen, setMessagesOpen] = useState(
    window.location.hash === "#messages"
  );
  const [identity, setIdentity] = useState(loadIdentity);
  // The identity form stays visible until the buyer has actually posted
  // with it (or arrived with a stored identity) — gating visibility on the
  // live field values would unmount the row mid-typing.
  const [identityConfirmed, setIdentityConfirmed] = useState(() => {
    const stored = loadIdentity();
    return !!(stored.name && stored.email);
  });
  // Composer scope: null | {kind, content_type?, content_id?, label} —
  // set by a card's Discuss button or the Request content button.
  const [composeContext, setComposeContext] = useState(null);
  // Milestone IDs with an in-flight toggle, so we can show a spinner.
  const [togglingMilestones, setTogglingMilestones] = useState({});

  const pollRef = useRef(null);
  const { track, startContentTimer, stopContentTimer } = useRoomTracking(
    token,
    isPreview
  );

  const unlockKey = `dsr_unlock_${token}`;
  const getUnlock = () => {
    try {
      return sessionStorage.getItem(unlockKey) || "";
    } catch (e) {
      return "";
    }
  };

  // Tracking consent: nothing is recorded (and no content shown) until the
  // buyer explicitly agrees; remembered per room in this browser.
  const consentKey = `dsr_consent_${token}`;
  const hasStoredConsent = () => {
    try {
      return localStorage.getItem(consentKey) === "1";
    } catch (e) {
      return false;
    }
  };
  const [consented, setConsented] = useState(hasStoredConsent);

  const buyerUrl = useCallback(
    (path = "") => {
      const params = new URLSearchParams();
      if (isPreview) {
        params.set("preview", "1");
      }
      const unlock = getUnlock();
      if (unlock) {
        params.set("unlock", unlock);
      }
      const query = params.toString();
      return `${serverUrl}/api/v3/room/${token}${path}${query ? `?${query}` : ""}`;
    },
    // eslint-disable-next-line
    [token, isPreview]
  );

  const requestConfig = isPreview ? { withCredentials: true } : {};

  // Fire-and-forget identity binding so the seller's analytics show people
  // instead of anonymous visitor IDs.
  const identifyVisitor = useCallback(
    (email, name) => {
      if (!email || isPreview) {
        return;
      }
      fetch(buyerUrl("/identify"), {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({
          visitor_id: getVisitorId(),
          email,
          name: name || "",
        }),
      }).catch(() => {});
    },
    // eslint-disable-next-line
    [buyerUrl, isPreview]
  );

  const loadRoom = useCallback(() => {
    setIsLoading(true);
    setError(null);
    setGate(null);

    axios
      .get(buyerUrl(), requestConfig)
      .then((response) => {
        setRoom(response.data);
        setMilestones(response.data.milestones || []);
        setIsLoading(false);
        // The open is only recorded once the buyer has agreed to tracking
        // (returning visitors with stored consent record it immediately).
        if (hasStoredConsent()) {
          track({ event_type: "room_opened" });
        }
      })
      .catch((_error) => {
        setIsLoading(false);
        const code = _error.response?.data?.error;
        if (code === "password_required") {
          setGate("password");
        } else if (code === "email_required") {
          setGate("email");
        } else {
          setError(code || "network");
        }
      });
    // eslint-disable-next-line
  }, [token, isPreview]);

  useEffect(() => {
    // A clicked magic link lands here with ?verify= — exchange it for the
    // unlock proof, then clean the URL so refresh/bookmark doesn't re-send
    // a consumed (or expired) token.
    const verify = searchParams.get("verify");
    if (verify) {
      const stripVerify = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("verify");
        setSearchParams(next, { replace: true });
      };

      axios
        .post(`${serverUrl}/api/v3/room/${token}/unlock`, { verify })
        .then((response) => {
          try {
            sessionStorage.setItem(unlockKey, response.data.unlock || "");
          } catch (e) {
            // Storage unavailable — proof still applies to this load.
          }
          if (response.data.email) {
            setIdentity((prev) => ({ ...prev, email: response.data.email }));
            identifyVisitor(response.data.email, "");
          }
          stripVerify();
          loadRoom();
        })
        .catch((_error) => {
          const code = _error.response?.data?.error;
          stripVerify();
          setIsLoading(false);
          setGate("email");
          setGateStage("input");
          setGateError(
            code === "verify_expired"
              ? "That access link has expired — request a new one below."
              : "That access link isn't valid — request a new one below."
          );
        });
      return;
    }

    loadRoom();
    // eslint-disable-next-line
  }, [token]);

  const submitUnlock = () => {
    setGateError(null);

    // Email gate: requesting entry sends a magic link to the invited inbox;
    // access is only granted when that link is clicked.
    if (gate === "email") {
      const email = gateEmail.trim();
      axios
        .post(`${serverUrl}/api/v3/room/${token}/unlock`, { email })
        .then(() => {
          setGateStage("sent");
        })
        .catch((_error) => {
          const code = _error.response?.data?.error;
          const messages = {
            too_many_attempts:
              "Too many attempts — please wait a few minutes and try again.",
            email_not_invited:
              "That email isn't on the invite list for this room. Ask the person who shared it to add you.",
            invalid_email: "Please enter a valid email address.",
            send_failed:
              "The access email couldn't be sent right now. Please try again later or contact the person who shared this room.",
          };
          setGateError(messages[code] || "Something went wrong — please try again.");
        });
      return;
    }

    axios
      .post(`${serverUrl}/api/v3/room/${token}/unlock`, { password })
      .then((response) => {
        try {
          sessionStorage.setItem(unlockKey, response.data.unlock || "");
        } catch (e) {
          // Session-only fallback: reload still works within this call.
        }
        setPassword("");
        loadRoom();
      })
      .catch((_error) => {
        const code = _error.response?.data?.error;
        setGateError(
          code === "too_many_attempts"
            ? "Too many attempts — please wait a few minutes and try again."
            : "That passcode isn't right. Please try again."
        );
      });
  };

  // Messages: fetch when the panel opens, poll lightly while it stays open.
  const loadMessages = useCallback(() => {
    axios
      .get(buyerUrl("/messages"), requestConfig)
      .then((response) => {
        setMessages(response.data.rows || []);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [buyerUrl]);

  useEffect(() => {
    if (messagesOpen && room && (consented || isPreview)) {
      loadMessages();
      pollRef.current = setInterval(loadMessages, 20000);
      return () => clearInterval(pollRef.current);
    }
    return undefined;
    // eslint-disable-next-line
  }, [messagesOpen, room, consented]);

  const sendMessage = (body) => {
    if (!identity.name || !identity.email) {
      return Promise.reject(new Error("Please add your name and email first."));
    }

    try {
      localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
    } catch (e) {
      // Storage unavailable — the message still sends.
    }

    const payload = {
      author_name: identity.name,
      author_email: identity.email,
      body,
    };
    if (composeContext?.kind === "request") {
      payload.kind = "request";
    }
    if (composeContext?.content_type && composeContext?.content_id) {
      payload.content_type = composeContext.content_type;
      payload.content_id = composeContext.content_id;
    }

    return axios
      .post(buyerUrl("/messages"), payload, {
        headers: { "Content-Type": "text/plain" },
      })
      .then((response) => {
        setMessages((prev) => [...prev, response.data]);
        setIdentityConfirmed(true);
        identifyVisitor(identity.email, identity.name);
        setComposeContext(null);
      });
  };

  const acceptConsent = () => {
    try {
      localStorage.setItem(consentKey, "1");
    } catch (e) {
      // Storage unavailable — consent still applies to this page view.
    }
    setConsented(true);
    // First recorded event doubles as the consent audit trail.
    track({ event_type: "room_opened", metadata: { consented: true } });
  };

  const openScopedComposer = (context) => {
    setComposeContext(context);
    setMessagesOpen(true);
    const target = document.getElementById("messages");
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    }
  };

  const toggleMilestone = (milestone) => {
    if (isPreview || milestone.assignee !== "buyer") {
      return;
    }
    // Ignore repeat clicks while a toggle is already in flight.
    if (togglingMilestones[milestone.id]) {
      return;
    }

    const status = milestone.status === "done" ? "pending" : "done";
    setTogglingMilestones((prev) => ({ ...prev, [milestone.id]: true }));

    axios
      .post(
        buyerUrl(`/milestones/${milestone.id}`),
        { status, visitor_id: getVisitorId() },
        { headers: { "Content-Type": "text/plain" } }
      )
      .then((response) => {
        setMilestones(response.data.milestones || []);
      })
      .catch(() => {})
      .finally(() => {
        setTogglingMilestones((prev) => {
          const next = { ...prev };
          delete next[milestone.id];
          return next;
        });
      });
  };

  const openContentItem = (item) => {
    if (newTabRoutes[item.type]) {
      track({
        event_type: item.type === "link" ? "link_clicked" : "content_viewed",
        content_type: item.type,
        content_id: Number(item.id),
      });
      window.open(`${window.location.origin}${newTabRoutes[item.type](item)}`, "_blank");
      return;
    }

    switch (item.type) {
      case "link":
        track({
          event_type: "link_clicked",
          content_type: item.type,
          content_id: Number(item.id),
        });
        if (item.url) {
          window.open(item.url, "_blank");
        }
        break;

      case "document":
      case "video":
      case "podcast":
      case "gallery":
        startContentTimer(item);
        setOpenItem(item);
        break;

      default:
        break;
    }
  };

  const closeViewer = () => {
    stopContentTimer();
    setOpenItem(null);
  };

  if (isLoading) {
    return (
      <main className="buyer-room screen">
        <CompanyappLoader />
      </main>
    );
  }

  if (gate) {
    const isEmailGate = gate === "email";
    const linkSent = isEmailGate && gateStage === "sent";

    return (
      <main className="buyer-room buyer-room--gate screen">
        <Container>
          <div className="buyer-room__gate-card">
            <LockIcon className="buyer-room__gate-icon" />
            <Typography owner="client" variant="h5" className="mb-2">
              {linkSent
                ? "Check your email"
                : isEmailGate
                ? "This room is for invited people"
                : "This room is passcode protected"}
            </Typography>
            <Typography owner="client" variant="body2" className="mb-3">
              {linkSent
                ? `We've sent a secure access link to ${gateEmail.trim()}. Open it on this device to enter the room. The link is valid for 24 hours.`
                : isEmailGate
                ? "Enter the email address the room was shared with and we'll send you a secure access link."
                : "Enter the passcode you received from the person who shared this room with you."}
            </Typography>

            {!linkSent && isEmailGate && (
              <TextField
                className="w-100 mb-2"
                type="email"
                size="small"
                label="Email address"
                value={gateEmail}
                autoFocus
                onChange={(e) => setGateEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    submitUnlock();
                  }
                }}
              />
            )}
            {!isEmailGate && (
              <TextField
                className="w-100 mb-2"
                type="password"
                size="small"
                label="Passcode"
                value={password}
                autoFocus
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    submitUnlock();
                  }
                }}
              />
            )}

            {gateError && (
              <Typography owner="client" variant="body2" color="error" className="d-block mb-2">
                {gateError}
              </Typography>
            )}

            {linkSent ? (
              <>
                <Button
                  variant="contained"
                  className="w-100 buyer-room__btn mb-2"
                  onClick={submitUnlock}
                >
                  <Typography owner="client" variant="button">
                    Re-send the link
                  </Typography>
                </Button>
                <Button
                  variant="outlined"
                  className="w-100 buyer-room__btn buyer-room__btn--outlined"
                  onClick={() => {
                    setGateStage("input");
                    setGateError(null);
                  }}
                >
                  <Typography owner="client" variant="button">
                    Use a different email
                  </Typography>
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                className="w-100 buyer-room__btn"
                disabled={isEmailGate ? !gateEmail.trim() : !password}
                onClick={submitUnlock}
              >
                <Typography owner="client" variant="button">
                  {isEmailGate ? "Send access link" : "Enter room"}
                </Typography>
              </Button>
            )}
          </div>
        </Container>
      </main>
    );
  }

  if (error) {
    const copy = errorCopy[error] || errorCopy.network;
    return (
      <main className="buyer-room buyer-room--error screen">
        <Container>
          <div className="buyer-room__error-card">
            <Typography owner="client" variant="h4" className="mb-2">
              {copy.title}
            </Typography>
            <Typography owner="client" variant="body1">{copy.body}</Typography>
          </div>
        </Container>
      </main>
    );
  }

  // Past this point every branch reads room.* — the loading / gate / error
  // early-returns above should always cover a null room, but guard against
  // any transient state race so a missing room can never crash the page.
  if (!room) {
    return (
      <main className="buyer-room screen">
        <CompanyappLoader />
      </main>
    );
  }

  const accent = room.branding?.colour || "#0e6ba8";
  const logo = room.branding?.logo;
  const doneCount = milestones.filter((m) => m.status === "done").length;
  const sectionsWithItems = (room.sections || []).filter(
    (section) => (section.items || []).length > 0
  );
  const showIdentityForm = !identityConfirmed;

  // The general conversation vs. comments tied to a specific content item.
  // Content requests (no content_id) stay in the general conversation.
  const generalMessages = messages.filter(
    (m) => !m.content_type || !m.content_id
  );
  const itemCommentGroups = Object.values(
    messages
      .filter((m) => m.content_type && m.content_id)
      .reduce((acc, m) => {
        const key = `${m.content_type}:${m.content_id}`;
        if (!acc[key]) {
          acc[key] = {
            key,
            content_type: m.content_type,
            content_id: m.content_id,
            title: m.content_title || `${m.content_type} ${m.content_id}`,
            messages: [],
          };
        }
        acc[key].messages.push(m);
        return acc;
      }, {})
  );

  // Per-item comment counts for the content cards: the room payload seeds
  // them (available before the thread loads); loaded messages override with
  // live counts so new posts increment immediately.
  const commentCounts = { ...(room.comment_counts || {}) };
  itemCommentGroups.forEach((group) => {
    commentCounts[group.key] = group.messages.length;
  });

  const introVideo = room.intro_video;
  const introVideoUrl =
    introVideo && introVideo.filename
      ? `${serverUrl}/shared/files/${encodeURIComponent(introVideo.filename)}?access_token=${introVideo.file_access_token}`
      : null;

  // Tracking disclosure — blocks all content until the buyer agrees.
  // Seller previews skip it (nothing is tracked in preview anyway).
  if (!consented && !room.preview) {
    return (
      <main
        className="buyer-room buyer-room--gate screen"
        style={{ "--dsr-accent": accent }}
      >
        <Container>
          <div className="buyer-room__gate-card buyer-room__gate-card--wide">
            {logo && (
              <img
                className="buyer-room__consent-logo"
                src={logo}
                alt={room.company || room.title}
              />
            )}
            <Typography owner="client" variant="h5" className="mb-3">
              Before you continue
            </Typography>
            <Typography
              owner="client"
              variant="body2"
              className="mb-3 text-start"
            >
              This room has been shared with you by{" "}
              {room.seller?.name || "the sales team"}
              {room.company ? ` for ${room.company}` : ""}. To help them assist
              you, your activity here — the content you view, time spent,
              downloads, and any messages you send — is recorded and shared
              with them.
            </Typography>
            <Typography
              owner="client"
              variant="body2"
              className="mb-4 text-start"
            >
              By continuing, you agree to this activity being tracked and to
              the terms and conditions of use of this room.
            </Typography>
            <Button
              variant="contained"
              className="w-100 buyer-room__btn"
              onClick={acceptConsent}
            >
              <Typography owner="client" variant="button">
                Agree and continue
              </Typography>
            </Button>
            <Typography
              owner="client"
              variant="caption"
              className="d-block mt-3"
            >
              If you don't agree, simply close this page — no activity has
              been recorded.
            </Typography>
          </div>
        </Container>
      </main>
    );
  }

  return (
    <main className="buyer-room screen" style={{ "--dsr-accent": accent }}>
      {room.preview && (
        <div className="buyer-room__preview-banner">
          <Typography owner="client" variant="body2">
            Preview — this is what buyers will see. Visits aren't tracked, and
            the room stays hidden until it's set to Active.
          </Typography>
        </div>
      )}

      <header className="buyer-room__hero">
        <Container>
          <Row className="align-items-center">
            <Col xs={12} lg={7}>
              <div className="buyer-room__hero-inner">
                {logo && (
                  <div className="buyer-room__logo-chip">
                    <img src={logo} alt={room.company || room.title} />
                  </div>
                )}
                <Typography owner="client" variant="h2" className="buyer-room__title">
                  {room.title}
                </Typography>
                {room.company && (
                  <Typography owner="client" variant="subtitle1" className="buyer-room__company">
                    Prepared for {room.company}
                    {room.seller?.name ? ` by ${room.seller.name}` : ""}
                  </Typography>
                )}
                {room.description && (
                  <Typography
                    owner="client"
                    variant="body1"
                    component="div"
                    className="buyer-room__lead"
                    dangerouslySetInnerHTML={{ __html: room.description }}
                  />
                )}
              </div>
            </Col>
            {(introVideoUrl || introVideo?.field_media_oembed_video) && (
              <Col xs={12} lg={5}>
                <div className="buyer-room__intro-card">
                  <div className="buyer-room__intro-video">
                    {introVideoUrl ? (
                      <video controls preload="metadata">
                        <source src={introVideoUrl} />
                      </video>
                    ) : (
                      <iframe
                        src={introVideo.field_media_oembed_video}
                        title="Introduction"
                        allowFullScreen
                      ></iframe>
                    )}
                  </div>
                  <Typography owner="client" variant="body2" className="buyer-room__intro-label">
                    A personal introduction
                    {room.seller?.name ? ` from ${room.seller.name}` : ""}
                  </Typography>
                </div>
              </Col>
            )}
          </Row>
        </Container>
      </header>

      {sectionsWithItems.length > 1 && (
        <nav className="buyer-room__nav">
          <Container>
            <div className="buyer-room__nav-links">
              {sectionsWithItems.map((section) => (
                <a href={`#section-${section.id}`} key={`nav-${section.id}`}>
                  {section.title || "Content"}
                </a>
              ))}
              {milestones.length > 0 && <a href="#plan">Our plan</a>}
              <a href="#messages" onClick={() => setMessagesOpen(true)}>
                Messages
              </a>
            </div>
          </Container>
        </nav>
      )}

      <Container className="buyer-room__content">

        {room?.sections?.map((section) => (
          <section
            className="buyer-room__section mb-5"
            key={section.id}
            id={`section-${section.id}`}
          >
            {section.title && (
              <Typography owner="client" variant="h5" className="buyer-room__section-title">
                {section.title}
              </Typography>
            )}
            {section.description && (
              <Typography
                owner="client"
                variant="body2"
                className="buyer-room__section-description mb-3"
              >
                {section.description}
              </Typography>
            )}
            <Row className="gy-3">
              {section.items.map((item) => {
                const meta = typeMeta[item.type] || typeMeta.document;
                const TypeIcon = meta.icon;
                const opensNewTab = !!newTabRoutes[item.type] || item.type === "link";
                const commentCount =
                  commentCounts[`${item.type}:${item.id}`] || 0;
                return (
                  <Col xs={12} sm={6} lg={3} key={`item-${item.type}-${item.id}`}>
                    <div
                      className="buyer-room__card"
                      role="button"
                      tabIndex={0}
                      onClick={() => openContentItem(item)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          openContentItem(item);
                        }
                      }}
                    >
                      <div className="buyer-room__card-thumbnail">
                        {item.thumbnail ? (
                          <img
                            src={
                              item.thumbnail.indexOf("http") === 0
                                ? item.thumbnail
                                : `${serverUrl}${item.thumbnail}`
                            }
                            alt={item.title}
                          />
                        ) : (
                          <TypeIcon className="buyer-room__card-icon" />
                        )}
                      </div>
                      <div className="buyer-room__card-body">
                        <Typography owner="client" variant="body1" className="buyer-room__card-title">
                          {item.title}
                        </Typography>
                        <div className="d-flex align-items-center justify-content-between">
                          <Typography owner="client" variant="caption" className="buyer-room__card-type">
                            {meta.label}
                            {item.type === "event" && item.field_start_date
                              ? ` · ${moment(item.field_start_date).format("DD MMM YYYY")}`
                              : ""}
                            {opensNewTab && (
                              <OpenNewIcon className="buyer-room__card-open" />
                            )}
                          </Typography>
                          {!room.preview ? (
                            <IconButton
                              size="small"
                              className="buyer-room__card-discuss"
                              title={
                                commentCount > 0
                                  ? `${commentCount} comment${
                                      commentCount === 1 ? "" : "s"
                                    } · add yours`
                                  : `Ask about "${item.title}"`
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                openScopedComposer({
                                  kind: "message",
                                  content_type: item.type,
                                  content_id: Number(item.id),
                                  label: `Discussing: ${item.title}`,
                                });
                              }}
                            >
                              <Badge
                                badgeContent={commentCount}
                                overlap="circular"
                                className="buyer-room__card-badge"
                              >
                                <ChatIcon fontSize="small" />
                              </Badge>
                            </IconButton>
                          ) : (
                            commentCount > 0 && (
                              <span
                                className="buyer-room__card-comment-count"
                                title={`${commentCount} comment${
                                  commentCount === 1 ? "" : "s"
                                }`}
                              >
                                <ChatIcon fontSize="small" />
                                {commentCount}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </section>
        ))}

        {room?.sections.length === 0 && (
          <Typography owner="client" variant="body1">
            No content has been shared in this room yet.
          </Typography>
        )}

        {milestones.length > 0 && (
          <section className="buyer-room__plan mb-5" id="plan">
            <Typography owner="client" variant="h5" className="buyer-room__section-title">
              Our plan
            </Typography>
            <div className="buyer-room__progress">
              <div
                className="buyer-room__progress-bar"
                style={{
                  width: `${milestones.length ? Math.round((doneCount / milestones.length) * 100) : 0}%`,
                }}
              />
            </div>
            <Typography owner="client" variant="caption" className="buyer-room__progress-label">
              {doneCount} of {milestones.length} steps complete
            </Typography>
            <div className="buyer-room__milestones mt-2">
              {milestones.map((milestone) => (
                <div
                  className={`buyer-room__milestone ${
                    milestone.status === "done" ? "buyer-room__milestone--done" : ""
                  }`}
                  key={milestone.id}
                >
                  {togglingMilestones[milestone.id] ? (
                    <span className="buyer-room__milestone-spinner">
                      <CircularProgress size={16} />
                    </span>
                  ) : (
                    <Checkbox
                      size="small"
                      checked={milestone.status === "done"}
                      disabled={
                        isPreview ||
                        milestone.assignee !== "buyer" ||
                        !!togglingMilestones[milestone.id]
                      }
                      onChange={() => toggleMilestone(milestone)}
                    />
                  )}
                  <div className="buyer-room__milestone-body">
                    <Typography owner="client" variant="body1">{milestone.title}</Typography>
                    <Typography owner="client" variant="caption">
                      {milestone.assignee === "buyer" ? "Your step" : "Handled by us"}
                      {milestone.due
                        ? ` · due ${moment.unix(milestone.due).format("DD MMM YYYY")}`
                        : ""}
                      {milestone.description ? ` — ${milestone.description}` : ""}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="buyer-room__messages mb-4" id="messages">
          <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
            <Typography owner="client" variant="h5" className="buyer-room__section-title mb-0">
              Messages
            </Typography>
            {!messagesOpen && (
              <Button
                size="small"
                variant="outlined"
                className="buyer-room__btn buyer-room__btn--outlined"
                onClick={() => setMessagesOpen(true)}
              >
                <Typography owner="client" variant="button">
                  Open conversation
                </Typography>
              </Button>
            )}
            {!room.preview && (
              <Button
                size="small"
                variant="outlined"
                className="buyer-room__btn buyer-room__btn--outlined"
                onClick={() =>
                  openScopedComposer({
                    kind: "request",
                    label: "Content request — tell us what you need",
                  })
                }
              >
                <Typography owner="client" variant="button">
                  Request content
                </Typography>
              </Button>
            )}
          </div>
          {messagesOpen && (
            <RoomThread
              messages={generalMessages}
              onSend={sendMessage}
              canPost={!room.preview}
              mine="buyer"
              scope={
                composeContext
                  ? {
                      label: composeContext.label,
                      onClear: () => setComposeContext(null),
                    }
                  : null
              }
              emptyText="No messages yet — ask a question and the team will be notified."
              identitySlot={
                showIdentityForm ? (
                  <div className="buyer-room__identity mb-3">
                    <Typography owner="client" variant="body2" className="d-block mb-2">
                      Add your details so the team knows who's writing. Your
                      name and email are shared with the participants of this
                      room.
                    </Typography>
                    <div className="buyer-room__identity-fields">
                      <TextField
                        size="small"
                        label="Your name"
                        value={identity.name}
                        onChange={(e) =>
                          setIdentity({ ...identity, name: e.target.value })
                        }
                      />
                      <TextField
                        size="small"
                        label="Your email"
                        type="email"
                        value={identity.email}
                        onChange={(e) =>
                          setIdentity({ ...identity, email: e.target.value })
                        }
                      />
                    </div>
                  </div>
                ) : null
              }
            />
          )}
        </section>

        {messagesOpen && itemCommentGroups.length > 0 && (
          <section className="buyer-room__comments mb-4">
            <Typography
              owner="client"
              variant="h5"
              className="buyer-room__section-title mb-1"
            >
              Comments on shared content
            </Typography>
            <Typography
              owner="client"
              variant="body2"
              className="buyer-room__comments-intro mb-3"
            >
              Questions and notes tied to a specific item, kept separate from
              the main conversation.
            </Typography>
            {itemCommentGroups.map((group) => (
              <div className="buyer-room__comment-group" key={group.key}>
                <div className="buyer-room__comment-group-head">
                  <div>
                    <Typography
                      owner="client"
                      variant="body1"
                      className="buyer-room__comment-group-title"
                    >
                      {group.title}
                    </Typography>
                    <Typography owner="client" variant="caption">
                      {group.messages.length}{" "}
                      {group.messages.length === 1 ? "comment" : "comments"}
                    </Typography>
                  </div>
                  {!room.preview && (
                    <Button
                      size="small"
                      variant="outlined"
                      className="buyer-room__btn buyer-room__btn--outlined"
                      startIcon={<ChatIcon />}
                      onClick={() =>
                        openScopedComposer({
                          kind: "message",
                          content_type: group.content_type,
                          content_id: group.content_id,
                          label: `Discussing: ${group.title}`,
                        })
                      }
                    >
                      <Typography owner="client" variant="button">
                        Add comment
                      </Typography>
                    </Button>
                  )}
                </div>
                <RoomThread
                  messages={group.messages}
                  canPost={false}
                  mine="buyer"
                  owner="client"
                />
              </div>
            ))}
          </section>
        )}
      </Container>

      <footer className="buyer-room__footer">
        <Container className="py-4">
          <Typography owner="client" variant="caption" className="d-block mb-3">
            Shared privately with you{room.company ? ` and ${room.company}` : ""}.
            Interactions in this room (content views and messages) are visible
            to {room.seller?.name || "the sales team"} to help them assist you.
          </Typography>
          <a
            className="buyer-room__powered-by"
            href="https://companyapp.net"
            target="_blank"
            rel="noreferrer"
          >
            <Typography owner="client" variant="caption">
              Powered by
            </Typography>
            <img src={caLogo} alt="Companyapp" />
            <Typography owner="client" variant="caption" weight="medium">
              Companyapp
            </Typography>
          </a>
        </Container>
      </footer>

      {openItem && (
        <RoomItemViewer item={openItem} onClose={closeViewer} track={track} />
      )}
    </main>
  );
}
